import { mediaUploadManager, mediaOwnerKey } from "../media/createMediaUploadManager";
import { isMediaOptimizationEnabled, mediaApi } from "../media/mediaApi";
import { registerMedia } from "../media/mediaReferences";
import { servableCopyCount } from "../media/mediaPresets";
import type { MediaView } from "../media/mediaTypes";
import { postApi } from "../helpers/auth_helpers";
import { normalizeView } from "../pages/products/steps/photoViewNames";
import { persistDraftMedia, deleteDraftMedia, patchDraftMedia } from "./productDraftApi";
import { isProductDraftRevisionsEnabled } from "./productDraftRevisionsEnabled";
import { fileTypeFromMime, newMediaId } from "./fileTypeFromMime";
import {
  allUploadJobs,
  clearAllUploadJobs,
  deleteUploadJob,
  getUploadJob,
  putUploadJob,
  type StoredUploadJob,
} from "./productMediaUploadIdb";

export type UploadJobStatus = "QUEUED" | "UPLOADING" | "UPLOADED" | "FAILED";

/** Shown on a photo whose product no longer exists; retrying cannot help, so there is no Retry. */
export const PRODUCT_GONE = "This product was deleted";

class ProductGoneError extends Error {
  constructor() {
    super(PRODUCT_GONE);
  }
}

/** The draft-media save refused because the product is gone (deleted, or not in this studio). */
export const productGone = (saved: any) => !saved?.success && /^product not found/i.test(String(saved?.message || "").trim());

export type UploadJob = {
  mediaId: string;
  mediaAssetId?: string;
  productId: string;
  fileName: string;
  mime: string;
  section: string;
  processTitle: string;
  productProcessId?: string;
  show: number;
  pin: number;
  view?: string;
  status: UploadJobStatus;
  progress: number;
  previewUrl: string;
  storageUrl: string;
  error: string;
};

type Listener = (jobs: UploadJob[]) => void;

const MAX_CONCURRENT = 2;
const jobs = new Map<string, UploadJob>();
const cancelled = new Set<string>();
const listeners = new Set<Listener>();
let restoredFor = "";

export const uploadOwnerKeyFromAuth = (raw: string | null): string => {
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    const id = String(parsed?.users?._id || parsed?.users?.id || parsed?.id || "").trim();
    const studio = String(parsed?.users?.organizationResponse?._id || parsed?.users?.organizationResponse?.id || "").trim();
    return studio && id ? studio + ":" + id : id;
  } catch {
    return "";
  }
};

export const currentUploadOwnerKey = (): string => {
  if (typeof localStorage === "undefined") {
    return "";
  }
  return uploadOwnerKeyFromAuth(localStorage.getItem("authUser"));
};

export const isUploadJobForOwner = (
  row: { ownerKey?: string },
  ownerKey: string
): boolean => Boolean(ownerKey) && row.ownerKey === ownerKey;

export const isMediaUploadCancelled = (mediaId: string): boolean =>
  Boolean(mediaId) && cancelled.has(mediaId);

export const cancelProductMedia = async (mediaId: string) => {
  const id = String(mediaId || "").trim();
  if (!id) {
    return;
  }
  cancelled.add(id);
  const removed = jobs.get(id);
  if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
  jobs.delete(id);
  emit();
  try {
    await deleteUploadJob(id);
  } catch {
    // IndexedDB is missing in some tests and private browsers
  }
};

const emit = () => {
  const snapshot = Array.from(jobs.values()).map((job) => ({ ...job }));
  listeners.forEach((fn) => fn(snapshot));
};

const putMemory = (job: UploadJob) => {
  jobs.set(job.mediaId, job);
  emit();
};

const putXhr = (url: string, file: Blob, contentType: string, onProgress: (pct: number) => void) =>
  new Promise<{ status: number; url: string; body: string }>((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      resolve({ status: xhr.status, url: url.split("?")[0], body: xhr.responseText || "" });
    };
    xhr.onerror = () => {
      resolve({ status: 0, url: url.split("?")[0], body: "" });
    };
    xhr.send(file);
  });

const legacyPresign = async (mime: string) => {
  const response = await postApi(
    `${String(process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "")}/upload/productDesigningPresignedURL`,
    { fileType: fileTypeFromMime(mime) }
  );
  return String(response?.data || "");
};

/** The tag the studio has chosen by now; a photo can be tagged while its upload is still running. */
const currentView = (job: UploadJob) => jobs.get(job.mediaId)?.view || job.view || undefined;

/** The draft row was saved with `sent`; if the studio tagged the photo meanwhile, patch the row. */
const settleView = async (job: UploadJob, sent: string | undefined) => {
  const now = currentView(job);
  if ((now || "") === (sent || "") || !job.productId) return;
  await patchDraftMedia(job.productId, job.mediaId, { view: now || "" });
};

const DELIVERY_POLL_MS = 5000;
const DELIVERY_POLL_TRIES = 6;
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The master-complete view has no small copies yet, so a tile rendered from it shows the 2 MB master.
 * Once the copies exist (usually by the time the original is archived; the worker runs every 10 s),
 * register the full view and nudge the rows so `HKPImage` picks the right size without a reload.
 */
export const refreshMediaDelivery = async (
  mediaId: string,
  assetId: string,
  first: Promise<MediaView | undefined> | undefined,
  pollMs = DELIVERY_POLL_MS
): Promise<MediaView | undefined> => {
  const owner = currentUploadOwnerKey();
  let view = await (first || Promise.resolve(undefined)).catch(() => undefined);
  for (let attempt = 0; !(view && servableCopyCount(view)) && attempt < DELIVERY_POLL_TRIES; attempt++) {
    await wait(pollMs);
    if (cancelled.has(mediaId) || currentUploadOwnerKey() !== owner) return undefined;
    try {
      view = await mediaApi.get(assetId);
    } catch {
      view = undefined;
    }
  }
  if (!view || !servableCopyCount(view) || currentUploadOwnerKey() !== owner) return undefined;
  registerMedia(view);
  const live = jobs.get(mediaId);
  if (live) putMemory({ ...live });
  return view;
};

const runJob = async (job: UploadJob) => {
  if (cancelled.has(job.mediaId)) {
    await deleteUploadJob(job.mediaId);
    return;
  }
  const stored = await getUploadJob(job.mediaId);
  const blob = stored?.blob;
  if (!blob) {
    putMemory({ ...job, status: "FAILED", error: "File is no longer on this device" });
    return;
  }
  putMemory({ ...job, status: "UPLOADING", progress: 1, error: "" });
  await putUploadJob({ ...stored, status: "UPLOADING" });
  try {
    if (!stored?.ownerKey || stored.ownerKey !== currentUploadOwnerKey())
      throw new Error("Upload session changed; sign in to resume");
    if (isMediaOptimizationEnabled() && /^image\//.test(job.mime)) {
      if (!mediaOwnerKey()) throw new Error("Studio access is required for photo uploads");
      let assetId = stored.mediaAssetId;
      let media: MediaView;
      let archived: Promise<MediaView | undefined> | undefined;
      if (assetId) {
        media = await mediaApi.get(assetId);
        if (!media.operationalReady) throw new Error("Photo is not operationally ready");
      } else {
        const file = new File([blob], job.fileName, { type: job.mime });
        const result = await mediaUploadManager.upload(file,
          job.section === "thumbnail" ? "PRODUCT_PRIMARY" : "PROCESS_EVIDENCE");
        assetId = result.mediaId;
        media = result.media;
        archived = result.archive;
        result.disposePreview();
        await putUploadJob({ ...stored, mediaAssetId: assetId, status: "UPLOADING" });
      }
      if (cancelled.has(job.mediaId)) return;
      if (stored.ownerKey !== currentUploadOwnerKey()) throw new Error("Upload session changed");
      registerMedia(media);
      if (!servableCopyCount(media)) void refreshMediaDelivery(job.mediaId, assetId, archived);
      if (job.productId && isProductDraftRevisionsEnabled()) {
        const view = currentView(job);
        const saved = await persistDraftMedia(job.productId, {
          mediaId: job.mediaId, mediaAssetId: assetId,
          section: job.section, processTitle: job.processTitle,
          productProcessId: job.productProcessId, show: job.show, pin: job.pin, mediaType: "image",
          ...(view ? { view } : {}),
        });
        if (cancelled.has(job.mediaId)) { await deleteDraftMedia(job.productId, job.mediaId); return; }
        if (productGone(saved)) throw new ProductGoneError();
        if (!saved?.success) throw new Error(saved?.message || "Could not save media to the product");
        await settleView(job, view);
      }
      if (stored.ownerKey !== currentUploadOwnerKey()) return;
      await deleteUploadJob(job.mediaId);
      URL.revokeObjectURL(job.previewUrl);
      putMemory({ ...job, mediaAssetId: assetId, status: "UPLOADED", progress: 100,
        previewUrl: "", storageUrl: media.masterUrl || "", error: "" });
      return;
    }
    const contentType = fileTypeFromMime(job.mime);
    // ponytail: nested draft keys (`upload-file/product/{uid}/{id}.ext`) PUT with progress
    // then fail in the browser. Studio already uploads via product_designing keys.
    // Upgrade: flatten the draft key to that same prefix depth, then switch back.
    const signed = await legacyPresign(job.mime);
    if (!signed) {
      throw new Error("Could not get upload URL");
    }
    if (cancelled.has(job.mediaId)) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    const put = await putXhr(signed, blob, contentType, (pct) => {
      const current = jobs.get(job.mediaId);
      if (current) {
        putMemory({ ...current, progress: pct });
      }
    });
    if (cancelled.has(job.mediaId)) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    if (put.status < 200 || put.status >= 300) {
      const hint = put.body ? `: ${put.body.replace(/\s+/g, " ").slice(0, 160)}` : "";
      throw new Error(put.status ? `Upload failed (${put.status})${hint}` : "Upload failed");
    }
    const storageUrl = put.url;
    if (cancelled.has(job.mediaId)) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    if (job.productId && isProductDraftRevisionsEnabled()) {
      const view = currentView(job);
      const saved = await persistDraftMedia(job.productId, {
        mediaId: job.mediaId,
        mediaUrl: storageUrl,
        section: job.section,
        processTitle: job.processTitle,
        productProcessId: job.productProcessId,
        show: job.show,
        pin: job.pin,
        mediaType: job.mime,
        ...(view ? { view } : {}),
      });
      if (cancelled.has(job.mediaId)) {
        await deleteDraftMedia(job.productId, job.mediaId);
        await deleteUploadJob(job.mediaId);
        return;
      }
      if (productGone(saved)) {
        throw new ProductGoneError();
      }
      if (!saved?.success) {
        throw new Error(saved?.message || "Could not save media to the product");
      }
      await settleView(job, view);
    }
    if (cancelled.has(job.mediaId)) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    await deleteUploadJob(job.mediaId);
    putMemory({
      ...job,
      status: "UPLOADED",
      progress: 100,
      storageUrl,
      error: "",
    });
  } catch (error: any) {
    if (stored?.ownerKey !== currentUploadOwnerKey()) return;
    if (cancelled.has(job.mediaId)) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    putMemory({
      ...job,
      status: "FAILED",
      error: error?.message || "Upload failed",
    });
    // Nothing to retry into: forget the stored job so it does not come back on the next visit.
    if (error instanceof ProductGoneError) {
      await deleteUploadJob(job.mediaId);
      return;
    }
    const latest = await getUploadJob(job.mediaId);
    await putUploadJob({ ...(latest || stored), status: "FAILED" });
  }
};

const pump = () => {
  const uploading = Array.from(jobs.values()).filter((j) => j.status === "UPLOADING");
  const slots = MAX_CONCURRENT - uploading.length;
  if (slots <= 0) {
    return;
  }
  const queued = Array.from(jobs.values())
    .filter((j) => j.status === "QUEUED" && !cancelled.has(j.mediaId))
    .slice(0, slots);
  queued.forEach((job) => {
    putMemory({ ...job, status: "UPLOADING", progress: 1, error: "" });
    void runJob({ ...job, status: "UPLOADING" }).finally(() => pump());
  });
};

export const subscribeUploadJobs = (fn: Listener): (() => void) => {
  listeners.add(fn);
  fn(Array.from(jobs.values()).map((job) => ({ ...job })));
  return () => {
    listeners.delete(fn);
  };
};

export const getUploadJobs = (): UploadJob[] => Array.from(jobs.values()).map((job) => ({ ...job }));

export const summarizeUploads = (productId?: string, visibleMediaIds?: Iterable<string>) => {
  const visible = visibleMediaIds ? new Set(Array.from(visibleMediaIds).filter(Boolean)) : null;
  const list = Array.from(jobs.values()).filter((job) => {
    if (productId !== undefined && job.productId !== productId) {
      return false;
    }
    return true;
  });
  const failed = list.filter((j) => j.status === "FAILED");
  const failedBlocking = visible
    ? failed.filter((j) => visible.has(j.mediaId))
    : failed;
  return {
    uploaded: list.filter((j) => j.status === "UPLOADED").length,
    uploading: list.filter((j) => j.status === "UPLOADING").length,
    queued: list.filter((j) => j.status === "QUEUED").length,
    failed: failed.length,
    unresolved:
      list.filter((j) => j.status === "QUEUED" || j.status === "UPLOADING").length +
      failedBlocking.length,
  };
};

/** Only a known photo view travels with an upload; VIDEO (a coverage slot) and typos would 400 the save. */
const photoView = (view: string | null | undefined): string | undefined => normalizeView(view) || undefined;

type EnqueueOptions = {
  file: File;
  productId: string;
  section?: string;
  processTitle?: string;
  productProcessId?: string;
  show?: number;
  pin?: number;
  mediaId?: string;
  view?: string | null;
};

/** The job each picked File went to; a File uploads once while that job is live (not deleted or cleared). */
const enqueuedFiles = new WeakMap<File, Promise<UploadJob | null>>();

/**
 * Pickers hand back every file picked so far, so the same File can arrive here again with the next pick.
 * It returns the job it already has instead of uploading the file a second time.
 */
export const enqueueProductMedia = (opts: EnqueueOptions): Promise<UploadJob | null> => {
  if (!(opts.file instanceof File)) {
    return Promise.resolve(null);
  }
  const earlier = enqueuedFiles.get(opts.file);
  // A pick that failed before it queued (browser storage full or blocked) tries again on the next pick.
  const next = earlier
    ? earlier.catch(() => null).then((job) => {
        const live = job ? jobs.get(job.mediaId) : undefined;
        return live ? { ...live } : enqueueFile(opts);
      })
    : enqueueFile(opts);
  enqueuedFiles.set(opts.file, next);
  return next;
};

const enqueueFile = async (opts: EnqueueOptions): Promise<UploadJob | null> => {
  if (!(opts.file instanceof File) || opts.file.size <= 0) {
    return null;
  }
  const mediaId = opts.mediaId || newMediaId();
  if (cancelled.has(mediaId)) {
    return null;
  }
  const previewUrl = URL.createObjectURL(opts.file);
  const job: UploadJob = {
    mediaId,
    productId: opts.productId || "",
    fileName: opts.file.name,
    mime: opts.file.type,
    section: opts.section || "thumbnail",
    processTitle: opts.processTitle || "",
    productProcessId: opts.productProcessId || "",
    show: opts.show === 0 ? 0 : 1,
    pin: opts.pin ? 1 : 0,
    ...(photoView(opts.view) ? { view: photoView(opts.view) } : {}),
    status: "QUEUED",
    progress: 0,
    previewUrl,
    storageUrl: "",
    error: "",
  };
  const stored: StoredUploadJob = {
    mediaId,
    productId: job.productId,
    ownerKey: currentUploadOwnerKey(),
    fileName: job.fileName,
    mime: job.mime,
    section: job.section,
    processTitle: job.processTitle,
    productProcessId: job.productProcessId,
    show: job.show,
    pin: job.pin,
    ...(job.view ? { view: job.view } : {}),
    status: "QUEUED",
    blob: opts.file,
  };
  await putUploadJob(stored);
  putMemory(job);
  void pump();
  return job;
};

export const retryJobSnapshot = (job: UploadJob): UploadJob => ({
  ...job,
  status: "QUEUED",
  progress: 0,
  storageUrl: "",
  error: "",
});

/** Tag (or untag) a photo whose upload is still queued, so the draft row is saved with that view. */
export const setProductMediaView = async (mediaId: string, view: string | null) => {
  const id = String(mediaId || "").trim();
  if (!id) return;
  const next = photoView(view);
  const job = jobs.get(id);
  if (job) putMemory({ ...job, view: next });
  const stored = await getUploadJob(id);
  if (stored) await putUploadJob({ ...stored, view: next });
};

/** Re-queues a job from memory or IndexedDB (also how a restored device copy gets its uploads going again). */
export const retryProductMedia = async (mediaId: string) => {
  const job = jobs.get(mediaId);
  const stored = await getUploadJob(mediaId);
  if (!job && !stored) {
    return;
  }
  const next = retryJobSnapshot({
    mediaId,
    productId: job?.productId || stored?.productId || "",
    fileName: job?.fileName || stored?.fileName || "",
    mime: job?.mime || stored?.mime || "",
    section: job?.section || stored?.section || "thumbnail",
    processTitle: job?.processTitle || stored?.processTitle || "",
    productProcessId: job?.productProcessId || stored?.productProcessId || "",
    show: job?.show ?? stored?.show ?? 1,
    pin: job?.pin ?? stored?.pin ?? 0,
    view: job?.view || stored?.view || undefined,
    status: "FAILED",
    progress: 0,
    // A job read back from IndexedDB has no preview in this tab yet; the upload revokes it when done.
    previewUrl: job?.previewUrl || (stored?.blob ? URL.createObjectURL(stored.blob) : ""),
    storageUrl: "",
    error: "",
  });
  if (stored) {
    await putUploadJob({ ...stored, status: "QUEUED" });
  }
  putMemory(next);
  void pump();
};

export const clearProductMediaUploads = async () => {
  mediaUploadManager.interruptAll();
  jobs.forEach(job => { if (job.previewUrl) URL.revokeObjectURL(job.previewUrl); });
  cancelled.clear();
  jobs.clear();
  restoredFor = "";
  emit();
  try {
    await clearAllUploadJobs();
  } catch {
    // IndexedDB is missing in some tests and private browsers
  }
};

export const restoreProductMediaUploads = async () => {
  const ownerKey = currentUploadOwnerKey();
  if (!ownerKey) {
    await clearProductMediaUploads();
    return;
  }
  if (restoredFor && restoredFor !== ownerKey) {
    cancelled.clear();
    jobs.clear();
    emit();
  }
  if (restoredFor === ownerKey) {
    return;
  }
  restoredFor = ownerKey;
  if (typeof indexedDB === "undefined") {
    return;
  }
  const stored = await allUploadJobs();
  stored.forEach((row) => {
    if (!isUploadJobForOwner(row, ownerKey)) {
      return;
    }
    if (jobs.has(row.mediaId)) {
      return;
    }
    if (row.status === "FAILED" && !row.blob) {
      void deleteUploadJob(row.mediaId);
      return;
    }
    putMemory({
      mediaId: row.mediaId,
      mediaAssetId: row.mediaAssetId,
      productId: row.productId,
      fileName: row.fileName,
      mime: row.mime,
      section: row.section,
      processTitle: row.processTitle,
      productProcessId: row.productProcessId,
      show: row.show,
      pin: row.pin,
      view: row.view || undefined,
      status: row.status === "FAILED" ? "FAILED" : row.status === "UPLOADED" ? "UPLOADED" : "QUEUED",
      progress: 0,
      previewUrl: row.blob ? URL.createObjectURL(row.blob) : "",
      storageUrl: "",
      error: row.status === "FAILED" ? "Upload failed" : "",
    });
  });
  void pump();
};

export const filesToEnqueue = (value: unknown): File[] => {
  if (typeof File !== "undefined" && value instanceof File && value.size > 0) {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(filesToEnqueue);
  }
  return [];
};

